/**
 * Step 8 · Sign & send (ux-flows §2 step 8, design-spec/07 plate 07) — the ceremony.
 *
 * The emotional peak of the product and its one legal keystone. Everything on this screen is in
 * service of two things: the referee understands exactly what they are attesting to, and the act of
 * signing feels like giving your word rather than filing a form.
 *
 * **Nothing here is authored locally.** The attestation wording, the version it is published under,
 * and the name the signature is checked against all come from `GET /api/referee/sign`. That is the
 * backend's integrity design and this screen is the half of it that shows: the server freezes the
 * SAME constant it served into the immutable signature row, so a referee can never sign a sentence
 * they were not shown — but only as long as this screen actually shows it. Hence the attestation is
 * rendered in full, not collapsed behind a disclosure.
 *
 * **The expected name is shown, and deliberately NOT pre-filled.** The server compares the typed
 * name to the confirmed signer name after trim + whitespace-collapse + case-fold — and folds
 * nothing else, so "Dr Jane Smith" or "Jane R Smith" against a confirmed "Jane Smith" is a 422.
 * That makes showing the name mandatory (otherwise the rule is a trap at the last step). It does
 * NOT make pre-filling it right: the typed name IS the signature, and its whole evidentiary value
 * is that the signer typed their own name. A pre-filled box would produce a signature nobody typed.
 * So: show it plainly, ask them to type it, and make a mismatch a retype rather than a wall.
 *
 * **The mismatch now HAS a path back (WP-B15c).** It used not to: `confirm-details` requires status
 * `accepted`, by here the request is `drafted`, so a name typo'd at step 3 could only be signed by
 * retyping the typo — freezing a misspelling into an immutable legal row. `POST /sign/correct-name`
 * is the escape, and `NotMyName` below is where it surfaces. It is deliberately quiet: the common
 * case is a typo in what they TYPED, so retyping stays the primary path and correcting the name on
 * record is the smaller, secondary door beside it.
 *
 * **When the name is corrected, the LETTER is not.** The server answers `previousNameInLetter` when
 * the replaced name is still in the letter body — a real risk, since a body reading "…Sam Whitmoore"
 * over a signature block reading "Sam Whitmore" is one document naming two people. The letter is
 * never rewritten for them: it is the referee's own words, the draft editor is open at this exact
 * status, and regenerating would spend a paid model call on a letter they already approved. So this
 * screen WARNS and links to the editor. That is the whole of the contract — warn, never block.
 *
 * **Ordering of the ceremony (why the seal precedes the navigation).** The seal is stamped only
 * after the server has said yes — a seal that appeared on press and then had to be taken back on a
 * 422 would be a lie told at the exact moment this product sells trust. And the context is marked
 * `signed` only AFTER the ceremony has played: `canAccess` locks a signed request to Done, so
 * refreshing the context any earlier would bounce this screen mid-stamp.
 */
import { forwardRef, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Seal } from '@/components/Seal';
import { apiClient } from '@/lib/api-client';
import {
  CORRECT_NAME_ERROR_CANNOT_CORRECT,
  RefereeApiError,
  SIGN_ERROR_ATTESTATION_STALE,
  SIGN_ERROR_CANNOT_SIGN,
  SIGN_ERROR_NAME_MISMATCH,
  SIGN_ERROR_UNKNOWN_DOCUMENT,
  type RefereeContext,
} from '@/lib/api-types';
import { CEREMONY_HOLD_MS, usePrefersReducedMotion } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useSessionStore } from '@/session/store';
import { refereeContextQueryKey, useSession } from '@/session/useSession';

/**
 * framer-motion's ONLY entry point into this app, behind a dynamic import (see `SealStamp.tsx`).
 * `lazy()` here is what puts ~34kB gzip of animation library in its own chunk instead of the
 * first-paint payload — the design law is lightness on mobile data, and this is the one screen
 * allowed to spend anything on motion at all.
 */
const SealStamp = lazy(() => import('@/components/SealStamp'));

/** Local to this screen — the disclosures are fetched nowhere else, so the key is shared with nobody. */
function signQueryKey(token: string) {
  return ['referee-sign-disclosures', token] as const;
}

/**
 * The errors whose only correct handling is leaving: `conflict` (a 409 — the request is not in a
 * signable state) and `unauthorized` (a 401 the transport could not heal by re-minting the
 * session, which means the request left the resumable states — the same situation wearing the
 * session's clothes). Both re-read the context and let the step guard relocate.
 */
function isLeaveKind(error: unknown): boolean {
  return error instanceof RefereeApiError && (error.kind === 'conflict' || error.kind === 'unauthorized');
}

export function Sign() {
  const { token, context } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setSignResult = useSessionStore((s) => s.setSignResult);
  const reducedMotion = usePrefersReducedMotion();

  const [typedName, setTypedName] = useState('');
  /** True from the moment the server says yes — the seal is stamped and the form is done. */
  const [ceremony, setCeremony] = useState(false);
  /** Whether the "that's not my name" door is open (WP-B15c). Closed until asked for. */
  const [correcting, setCorrecting] = useState(false);
  /**
   * Set when a correction lands on a letter that still carries the OLD name — the referee is told,
   * and pointed at the editor. Survives closing the correction form: the letter is still stale
   * whether or not the box that reported it is on screen.
   */
  const [letterStale, setLetterStale] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disclosures = useQuery({
    queryKey: signQueryKey(token),
    queryFn: () => apiClient.getSignDisclosures(token),
    enabled: token.length > 0,
    staleTime: 60_000,
    // A 409 `cannot_sign` is a settled answer about state, not a blip — retrying it just delays
    // the redirect that should already be happening.
    retry: false,
  });

  /**
   * The request is no longer signable — almost always signed in another tab, or withdrawn/expired
   * underneath them. Re-read the context and let the step guard put them somewhere true; there is
   * nothing to say on this screen about a request it cannot sign. See `isLeaveKind` for the two
   * error kinds that land here.
   */
  const leaveForTruth = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: refereeContextQueryKey(token) });
  }, [queryClient, token]);

  const disclosuresError = disclosures.error;
  useEffect(() => {
    if (isLeaveKind(disclosuresError)) leaveForTruth();
  }, [disclosuresError, leaveForTruth]);

  useEffect(() => () => void (timerRef.current && clearTimeout(timerRef.current)), []);

  const sign = useMutation({
    mutationFn: (name: string) => {
      const d = disclosures.data;
      if (!d) throw new RefereeApiError('Disclosures not loaded', 'server');
      // Echo the attestation pair from the disclosures THIS screen displayed — never a constant.
      return apiClient.sign(token, {
        typedName: name,
        documentKey: d.documentKey,
        documentVersion: d.documentVersion,
      });
    },
    onSuccess: (result) => {
      // A 200 here is success whether it was the first signature or an idempotent repeat — the
      // backend answers both identically, carrying the ORIGINAL code. There is no "already signed"
      // error to handle, and a double-tap must never read as a failure.
      setSignResult(result);
      setCeremony(true);

      // The wall clock is the authority for moving on — NOT the animation's completion callback.
      // A backgrounded tab throttles rAF, a lazy chunk can fail to load, and the Suspense fallback
      // has no animation to complete: in every one of those cases a signature that succeeded must
      // still reach the done screen. The seal is decoration over this timer, never a gate on it.
      timerRef.current = setTimeout(
        () => {
          // Mark the context signed from the fact we were just handed, rather than refetching:
          // a 200 from sign IS the server saying `signed`, so this needs no network and cannot
          // fail — which matters, because a failed refetch here would leave a signed referee
          // being bounced back to Review by the step guard. Done reads the code from the store.
          queryClient.setQueryData(
            refereeContextQueryKey(token),
            (prev: RefereeContext | undefined) => (prev ? { ...prev, status: 'signed' as const } : prev),
          );
          navigate(`/r/${token}/done`, { replace: true });
        },
        reducedMotion ? 0 : CEREMONY_HOLD_MS,
      );
    },
    onError: (error) => {
      if (!(error instanceof RefereeApiError)) return;
      if (error.code === SIGN_ERROR_CANNOT_SIGN || isLeaveKind(error)) {
        leaveForTruth();
        return;
      }
      if (error.code === SIGN_ERROR_ATTESTATION_STALE) {
        // The wording moved under them. Re-fetch so what is on screen is what a signature would
        // now bind to; they can then sign the text they can actually see.
        void disclosures.refetch();
        return;
      }
      if (error.code === SIGN_ERROR_NAME_MISMATCH) {
        // Put them back in the box they need to retype, without clearing what they typed — it is
        // usually nearly right, and wiping it would make a small typo feel like starting over.
        inputRef.current?.focus();
      }
    },
  });

  /**
   * The name correction (WP-B15c). On success the disclosures are re-fetched rather than patched
   * locally: `signerName` is the string the server will compare a signature against, so it must
   * come back from the server that will do the comparing — a locally-invented one that drifted
   * would put the referee back in the exact trap this verb exists to open.
   */
  const correctName = useMutation({
    mutationFn: (fullName: string) => apiClient.correctName(token, { fullName }),
    onSuccess: async (result) => {
      setLetterStale(result.previousNameInLetter);
      setCorrecting(false);
      // Whatever they typed was checked against the OLD name; clear it so they type afresh against
      // the new one rather than re-submitting a value that is now measured differently.
      setTypedName('');
      sign.reset();
      await disclosures.refetch();
    },
    onError: (error) => {
      // Signed in another tab mid-correction, or otherwise no longer correctable (including an
      // unhealable 401 — same situation, surfaced by the session instead of the verb). There is
      // nothing to fix on a request this screen can't sign — go somewhere true.
      if (
        (error instanceof RefereeApiError && error.code === CORRECT_NAME_ERROR_CANNOT_CORRECT) ||
        isLeaveKind(error)
      ) {
        leaveForTruth();
      }
    },
  });

  const signerName = disclosures.data?.signerName ?? '';
  const candidateName = context?.candidate.name ?? 'them';
  const ready = !!disclosures.data && !sign.isPending && !ceremony && !correctName.isPending;
  const canSubmit = ready && typedName.trim().length > 0;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    sign.mutate(typedName);
  };

  return (
    <div className="flex flex-1 flex-col">
      {!ceremony && (
        <Link
          to={`/r/${token}/review`}
          className="-ml-1 inline-flex min-h-[44px] items-center self-start rounded-md px-1 font-display text-[12.5px] font-medium text-ink-70 transition-colors duration-ui hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
        >
          &lsaquo; Back to your letter
        </Link>
      )}

      <h1 className="mt-1 font-display text-[19px] font-semibold leading-[1.28] tracking-[-0.02em] text-ink text-balance">
        By signing, you&rsquo;re confirming this is your honest reference for {candidateName}.
      </h1>

      {disclosures.isLoading && <DisclosuresSkeleton />}

      {/* No retry box for the leave kinds: the guard is already relocating them, and "Try again"
          on a request that cannot be signed would only re-refuse. */}
      {disclosures.isError && !isLeaveKind(disclosuresError) && (
        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p role="alert" className="font-body text-[13px] leading-relaxed text-ink-70">
            We couldn&rsquo;t load the signing details just now.
          </p>
          <Button variant="ghost" className="mt-3" onClick={() => void disclosures.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {disclosures.data && (
        <form onSubmit={onSubmit} className="flex flex-1 flex-col">
          <fieldset disabled={ceremony} className="contents">
            <NameField
              ref={inputRef}
              value={typedName}
              signerName={signerName}
              onChange={setTypedName}
              mismatch={sign.error instanceof RefereeApiError && sign.error.code === SIGN_ERROR_NAME_MISMATCH}
            />

            <NotMyName
              open={correcting}
              pending={correctName.isPending}
              failed={correctName.isError}
              signerName={signerName}
              onOpen={() => setCorrecting(true)}
              onCancel={() => {
                setCorrecting(false);
                correctName.reset();
              }}
              onSubmit={(name) => correctName.mutate(name)}
            />

            {letterStale && <LetterStaleNotice token={token} />}

            <SignaturePreview
              typedName={typedName}
              signerName={signerName}
              meta={signatureMeta(context)}
              sealed={ceremony}
              reducedMotion={reducedMotion}
            />

            <Attestation text={disclosures.data.attestationText} />

            <p className="mt-3 font-body text-[12px] leading-[1.5] text-ink-70">
              Once you sign, {candidateName} can keep and reuse this letter.{' '}
              <a
                href="https://bymyword.com/terms"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-sage-text underline-offset-2 hover:underline"
              >
                Terms
              </a>
            </p>

            <div className="mt-auto flex flex-col gap-2.5 pt-6">
              <SignError error={sign.error} signerName={signerName} />

              <p className="text-center font-display text-[11.5px] font-medium text-ink-70">
                Nothing reaches {candidateName} until this moment.
              </p>

              {/* The one sage action in the whole referee flow — every other CTA is coral. */}
              <Button type="submit" variant="default" size="block" disabled={!canSubmit}>
                {ceremony ? 'Signed' : sign.isPending ? 'Signing…' : 'Sign & send'}
              </Button>
            </div>
          </fieldset>
        </form>
      )}
    </div>
  );
}

/**
 * "Former Manager · Northwind Studio · 17 July 2026" — the line beneath the signature.
 *
 * The date is TODAY's, formatted in the house style (design-spec/04: `14 July 2026`). It is a
 * preview of what the letter will carry, not a claim about a signature that has not happened yet —
 * the authoritative `signed_at` is stamped server-side inside the sign transaction, and this screen
 * never sees it until the response comes back.
 */
function signatureMeta(context: RefereeContext | undefined): string {
  const today = new Date().toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
  return [context?.referee.roleThen, context?.referee.company, today].filter(Boolean).join(' · ');
}

/* ---------------------------------------------------------------------------------------------- *
 * The name field — where the trap lives, so where the guidance lives
 * ---------------------------------------------------------------------------------------------- */

interface NameFieldProps {
  value: string;
  signerName: string;
  onChange: (value: string) => void;
  mismatch: boolean;
}

const NameField = forwardRef<HTMLInputElement, NameFieldProps>(function NameField(
  { value, signerName, onChange, mismatch },
  ref,
) {
  return (
    <div className="mt-5">
      <label htmlFor="typed-name" className="font-body text-[13px] font-medium text-ink">
        Type your full name to sign
      </label>

      {/* The expected name, shown plainly and at full contrast. The server refuses anything else,
          so this is not a hint — it is the instruction. Not pre-filled: see the module remarks. */}
      <p id="typed-name-hint" className="mt-1 font-body text-[12.5px] leading-[1.45] text-ink-70">
        As you confirmed it: <span className="font-semibold text-ink">{signerName}</span>
      </p>

      <input
        id="typed-name"
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={signerName}
        autoComplete="name"
        autoCapitalize="words"
        spellCheck={false}
        aria-describedby="typed-name-hint"
        aria-invalid={mismatch || undefined}
        className={cn(
          'mt-2 block min-h-[48px] w-full rounded-md border bg-surface px-3.5 font-body text-[15px] font-medium text-ink',
          'placeholder:font-normal placeholder:text-ink-quiet',
          'transition-colors duration-ui ease-thread',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
          mismatch ? 'border-coral-deep' : value.trim() ? 'border-sage bg-surface-tint' : 'border-line-2',
        )}
      />
    </div>
  );
});

/* ---------------------------------------------------------------------------------------------- *
 * "That's not my name" — the escape the mismatch could not offer before (WP-B15c)
 * ---------------------------------------------------------------------------------------------- */

interface NotMyNameProps {
  open: boolean;
  pending: boolean;
  failed: boolean;
  signerName: string;
  onOpen: () => void;
  onCancel: () => void;
  onSubmit: (fullName: string) => void;
}

/**
 * The secondary door beside the name field.
 *
 * **Why it is a quiet link and not a button.** The overwhelmingly common cause of a mismatch is a
 * typo in what they just TYPED, and the fix for that is to retype — which the field above already
 * asks for. Giving "change the name on record" equal weight would invite people to edit the name a
 * legal document is attributed to whenever they fumbled a keystroke. So it sits low-contrast and
 * closed until asked for, and opens into a real form rather than doing anything on one tap.
 *
 * **It is available before a mismatch, not only after one.** A referee who reads "As you confirmed
 * it: Sam Whitmoore" and sees their name misspelled should not have to deliberately fail a
 * signature to be offered the fix.
 *
 * The corrected name is NOT pre-filled with the current one: it would be an odd default (the
 * current value is the thing they are saying is wrong) and it invites a one-character edit of a
 * legal name. Empty, with the current value shown beside it, is the honest ask.
 */
function NotMyName({ open, pending, failed, signerName, onOpen, onCancel, onSubmit }: NotMyNameProps) {
  const [value, setValue] = useState('');
  const fieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      fieldRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          // Fresh on EVERY open. Text abandoned by Cancel and a submitted correction must not
          // pre-fill the next visit; resetting from the opening event also avoids a cascading
          // render from synchronously changing state inside the focus effect.
          setValue('');
          onOpen();
        }}
        className="mt-2 min-h-[44px] self-start rounded-md px-1 text-left font-body text-[12.5px] font-medium text-ink-70 underline-offset-2 transition-colors duration-ui hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
      >
        That&rsquo;s not my name
      </button>
    );
  }

  const trimmed = value.trim();
  return (
    <div className="mt-3 rounded-md border border-line bg-surface p-3.5">
      <label htmlFor="corrected-name" className="font-body text-[13px] font-medium text-ink">
        What&rsquo;s your name?
      </label>
      <p id="corrected-name-hint" className="mt-1 font-body text-[12px] leading-[1.45] text-ink-70">
        We have <span className="font-semibold text-ink">{signerName}</span>. Correct it here and
        it&rsquo;s the name your reference is signed in.
      </p>

      <input
        id="corrected-name"
        ref={fieldRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Enter here must correct the name, NEVER sign the reference.
          //
          // This block is load-bearing and its absence is invisible: this field sits inside the
          // sign <form> (it cannot have its own — nested forms are invalid HTML), and a text input
          // inside a form implicitly SUBMITS it on Enter. `type="button"` on the controls below
          // stops click-submission but does nothing about that, so without this a referee who had
          // filled the name field, then opened this box and pressed Enter, would sign their
          // reference from a field about something else. Caught by a test that only bites when the
          // sign button is already enabled — with it disabled the bug is unreachable and the test
          // is decorative.
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (!pending && value.trim()) onSubmit(value.trim());
        }}
        autoComplete="name"
        autoCapitalize="words"
        spellCheck={false}
        aria-describedby="corrected-name-hint"
        className="mt-2 block min-h-[48px] w-full rounded-md border border-line-2 bg-surface px-3.5 font-body text-[15px] font-medium text-ink transition-colors duration-ui ease-thread focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
      />

      {failed && (
        <p role="alert" className="mt-2 font-body text-[12.5px] font-medium leading-[1.5] text-coral-text">
          We couldn&rsquo;t update that just now. Please try again.
        </p>
      )}

      <div className="mt-3 flex gap-2">
        {/* Not `type="submit"`: this sits inside the sign <form>, and a submit here would fire the
            ceremony. The most consequential button in the product must never be reachable from the
            Enter key of a field that is about something else. */}
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="button" variant="secondary" onClick={() => onSubmit(trimmed)} disabled={pending || !trimmed}>
          {pending ? 'Saving…' : 'Use this name'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Shown after a correction when the letter still carries the name that was just replaced.
 *
 * The letter and the signature block would otherwise name two different people on one legal
 * document. It is a prompt, never a gate: the words are the referee's, the editor is one tap away,
 * and they can sign without touching it if the letter is right as it stands.
 */
function LetterStaleNotice({ token }: { token: string }) {
  return (
    <div role="status" className="mt-3 rounded-md border border-gold/40 bg-surface-tint p-3.5">
      <p className="font-body text-[12.5px] leading-[1.5] text-ink">
        Your letter still uses your old name. It&rsquo;s your letter, so we haven&rsquo;t touched it
        — worth a look before you sign.
      </p>
      <Link
        to={`/r/${token}/review`}
        className="mt-2 inline-flex min-h-[44px] items-center font-display text-[12.5px] font-medium text-sage-text underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
      >
        Check your letter
      </Link>
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * The signature + the seal
 * ---------------------------------------------------------------------------------------------- */

/**
 * The signature as it will appear — Caveat, which is the ONLY place Caveat is ever used
 * (design-spec/01: signatures only, never UI).
 *
 * The seal is absent until the server has said yes. Gold is the sign moment and nothing before it;
 * the review screen's dashed "Your signature — added when you sign" slot is the setup this pays off.
 */
function SignaturePreview({
  typedName,
  signerName,
  meta,
  sealed,
  reducedMotion,
}: {
  typedName: string;
  signerName: string;
  meta: string;
  sealed: boolean;
  reducedMotion: boolean;
}) {
  const shown = typedName.trim();
  return (
    <div className="relative mt-4 rounded-lg border border-line bg-surface-tint p-4">
      <div className="font-display text-[9px] font-semibold uppercase tracking-[0.12em] text-ink-quiet">
        Your signature
      </div>

      {/* Overflow-safe rather than the mock's `white-space:nowrap`: a long name on a 320px screen
          must wrap, not slide under the seal. */}
      <div className="mt-1.5 min-h-[38px] break-words pr-[72px] font-hand text-[32px] leading-[1.15] text-ink">
        {shown || <span className="text-ink-quiet">{signerName}</span>}
      </div>
      <div className="mr-[72px] mt-1 h-px bg-line-2" />
      {meta && <div className="mt-1.5 pr-[72px] font-body text-[11px] leading-[1.45] text-ink-quiet">{meta}</div>}

      {sealed && (
        <span className="absolute bottom-3 right-3">
          {reducedMotion ? (
            // A dignified, complete seal — not a broken screen, and not a stilled animation. The
            // library is never even fetched on this path.
            <Seal size="sig" label="By My Word" />
          ) : (
            <Suspense fallback={<Seal size="sig" label="By My Word" />}>
              <SealStamp size="sig" label="By My Word" />
            </Suspense>
          )}
        </span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * The attestation + errors
 * ---------------------------------------------------------------------------------------------- */

/**
 * The exact wording being attested to, straight from the server.
 *
 * Shown in full and never collapsed: the backend freezes this same string into the immutable
 * signature row on the premise that it was displayed, so hiding it behind a "read more" would
 * quietly break that premise. It is only ~60 words — two short paragraphs is not the "legal wall"
 * the flow spec warns against, and it is `ink-70` body text rather than caption grey because a
 * legal attestation is the last thing that should be set in the faintest ink on the screen.
 */
function Attestation({ text }: { text: string }) {
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="mt-4 rounded-md border border-line bg-surface p-3.5">
      {paragraphs.map((p, i) => (
        <p key={i} className={cn('font-body text-[12.5px] leading-[1.55] text-ink-70', i > 0 && 'mt-2')}>
          {p}
        </p>
      ))}
    </div>
  );
}

/** The letter's shape while the disclosures load — never a bare spinner. */
function DisclosuresSkeleton() {
  return (
    <div className="mt-5 flex flex-col gap-3" aria-hidden="true">
      <div className="skel h-[14px] w-[52%] rounded-[6px]" />
      <div className="skel h-[48px] w-full rounded-md" />
      <div className="skel h-[96px] w-full rounded-lg" />
    </div>
  );
}

/**
 * Every sign refusal, in the referee's language.
 *
 * `role="alert"` because each of these arrives as the RESULT of pressing the most consequential
 * button in the product — a screen-reader user must hear why nothing happened, at the moment it
 * doesn't. The name mismatch is the one that matters most: it is a retype, and the copy's whole job
 * is to make that obvious by naming the exact string to type.
 */
function SignError({ error, signerName }: { error: unknown; signerName: string }) {
  if (!(error instanceof RefereeApiError)) return null;

  if (error.code === SIGN_ERROR_NAME_MISMATCH) {
    // Still leads with the retype — that is the fix in almost every case. The correction door is
    // named rather than linked from here: it lives beside the field they are being sent back to,
    // and a second control inside an alert would compete with the one they actually need.
    return (
      <p role="alert" className="text-center font-body text-[12.5px] font-medium leading-[1.5] text-coral-text">
        That doesn&rsquo;t match the name on this reference. Type your name as shown:{' '}
        <span className="font-semibold">{signerName}</span> — or, if that&rsquo;s not your name, use
        &ldquo;That&rsquo;s not my name&rdquo; above.
      </p>
    );
  }
  if (error.code === SIGN_ERROR_ATTESTATION_STALE) {
    return (
      <p role="alert" className="text-center font-body text-[12.5px] font-medium leading-[1.5] text-coral-text">
        The wording above was just updated — please read it once more, then sign.
      </p>
    );
  }
  if (error.code === SIGN_ERROR_UNKNOWN_DOCUMENT) {
    return (
      <p role="alert" className="text-center font-body text-[12.5px] font-medium leading-[1.5] text-coral-text">
        We can&rsquo;t take a signature right now. Nothing is lost — please try again shortly.
      </p>
    );
  }
  // The leave kinds (`conflict`, unhealable `unauthorized`) are handled by leaving the screen —
  // not an error to shout about; anything else is a network/server blip worth a retry.
  if (isLeaveKind(error)) return null;
  return (
    <p role="alert" className="text-center font-body text-[12.5px] font-medium leading-[1.5] text-coral-text">
      We couldn&rsquo;t send that just now — your letter is safe. Please try again.
    </p>
  );
}
